import React, { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import { default as Form } from 'components/connectedForm';
import OverviewContainer from 'components/connectedForm/OverviewContainer';
import OverviewCard from 'components/connectedForm/OverviewCard';
import OverviewCardTitle from '../../components/connectedForm/OverviewCardTitle';
import OverviewCardDivider from 'components/connectedForm/OverviewCardDivider';
import OverviewCardSubTitle from '../../components/connectedForm/OverviewCardSubTitle';
import OverviewLabel from 'components/connectedForm/OverviewLabel';
import OverviewField from 'components/connectedForm/OverviewField';
import OverviewInlineField from '../../components/connectedForm/OverviewInlineField';
import getIn from 'components/connectedForm/utils/getIn';
import { formatDate } from 'utils/DateHelper';
import { getPrimaryContact } from '../../utils/getPrimary';
import { addressUtil } from 'utils/AddressUtils';
import ValueCard from '../../components/connectedForm/ValueCard';
import ReactTooltip from 'react-tooltip';
import PurchaseTransactionWorkspace from './PurchaseTransactionWorkspace';
import PurchaseInvoiceWorkspace from './PurchaseInvoiceWorkspace';
import WorkspaceModal from 'components/Workspace/WorkspaceModal';
import PurchaseTransactionOverdueInvoiceAmount from 'graphql/queries/supplier/PurchaseTransactionOverdueInvoiceAmount.graphql';
import theme from '../../styles/themes/default/defaultTheme';
import { format } from 'date-fns';
import { useEventDetails } from '../diary/DiaryHooks';
import CurrencyContext from '../../components/Context/CurrencyContext';
import { useCompany } from '../../utils/FetchConfig';
import AppContext from '../../components/Context/AppContext';
import { checkPermission } from '../../utils/OperationPermissions';

//Convenient hook to reduce code repetition when fetching totals.
export const useTotalQuery = (aggregateQuery, { filters = [], sumColumn, skip = false }) => {
  const { data } = useQuery(aggregateQuery, {
    variables: {
      filters: filters,
      aggregateCriteria: { sumColumns: [sumColumn] },
    },
    skip,
  });
  //Only querying for one column total, so can assume it's in index 0.
  return data?.listData?.totalColumns?.[0]?.sum || 0;
};

const SupplierOverviewTab = ({ awaitingApprovalSum, id, orders, outstandingSum, sidebar, tabPanelRef, values }) => {
  const [purchaseTransactionsState, setPurchaseTransactionsState] = useState({ modalOpen: false });
  const [purchaseInvoicesState, setPurchaseInvoicesState] = useState({ modalOpen: false });
  const company = useCompany();

  const app = useContext(AppContext);

  const transactionsPermitted = checkPermission({ app }, `SUPPLIER_TRANSACTIONS_VIEW`);
  const tasksPermitted = checkPermission({ app }, `TASK_VIEW`);
  const purchaseOrdersPermitted = checkPermission({ app }, `PURCHASING_ORDERS_VIEW`);

  const overdueBalanceVariables = {
    supplierId: id,
  };

  const supplierFilter = id
    ? {
        filterOperation: 'EQUALS',
        filterParameters: {
          field: 'supplier.id',
          value: id,
        },
      }
    : null;

  const { data: overdueBalanceData } = useQuery(PurchaseTransactionOverdueInvoiceAmount, {
    variables: overdueBalanceVariables,
    skip: !overdueBalanceVariables.supplierId,
    fetchPolicy: 'network-only',
  });

  const events = useEventDetails([supplierFilter], !supplierFilter);
  const overdueBalance = overdueBalanceData?.amount || 0.0;

  const disputedInvoicesFilters = [
    supplierFilter,
    { filterOperation: 'EQUALS', filterParameters: { field: 'held', value: true } },
    { filterOperation: 'GREATER_THAN', filterParameters: { field: 'outstandingAmount', value: 0 } },
  ];

  const overdueBalanceFilters = [
    supplierFilter,
    {
      filterOperation: 'LESS_THAN',
      filterParameters: { field: 'paymentDueDate', value: format(new Date(), 'yyyy-MM-dd') },
    },
    { filterOperation: 'EQUALS', filterParameters: { field: 'documentType', value: 'PURCHASE_INVOICE' } },
    {
      filterOperation: 'EQUALS',
      filterParameters: { field: 'documentType', value: 'PURCHASE_OPENING_BALANCE_INVOICE' },
    },
  ];

  const invoicesAwaitingApprovalFilters = [
    supplierFilter,
    { filterOperation: 'EQUALS', filterParameters: { field: 'documentStatus', value: 'DRAFT' } },
  ];

  const displayPurchaseTransactionModal = (filters) => {
    setPurchaseTransactionsState({ modalOpen: true, filters: filters });
  };

  const displayPurchaseInvoiceModal = (filters) => {
    setPurchaseInvoicesState({ modalOpen: true, filters: filters });
  };

  const primaryContact = getPrimaryContact(values);
  const cols = sidebar ? 1 : 1 / 3;
  const currency = values.currency || { description: 'GBP' };
  return (
    <CurrencyContext.Provider value={{ currency: values?.currency || company?.baseCurrency }}>
      <ReactTooltip id={'supplierOverviewTooltip'} />
      <WorkspaceModal
        component={PurchaseTransactionWorkspace}
        viewScreenName={'/purchases/PurchaseTransactionWorkspace/Modal'}
        modalState={purchaseTransactionsState}
        closeModal={() => {
          setPurchaseTransactionsState({ modalOpen: false });
        }}
      />
      <WorkspaceModal
        component={PurchaseInvoiceWorkspace}
        viewScreenName={'/purchases/PurchaseInvoicesWorkspace/Modal'}
        modalState={purchaseInvoicesState}
        closeModal={() => {
          setPurchaseInvoicesState({ modalOpen: false });
        }}
      />
      <OverviewContainer sidebar={sidebar}>
        <Form.Row>
          <Form.Fieldset cols={cols}>
            <OverviewCard>
              <OverviewCardTitle
                title="Contact information"
                linkTabLabel="Details"
                linkTabId="main"
                modal={sidebar}
                type="supplier"
                entityId={id}
                tabPanelRef={tabPanelRef}
              />
              <OverviewField value={values.reference} list={true} primary={true} type="text" />
              <OverviewField value={values.name} list={true} primary={true} type="text" />
              <OverviewField value={values.email || 'No email address'} list={true} type="text" />
              <OverviewField value={values.telephoneNumber || 'No telephone number'} list={true} type="text" />
              <OverviewField value={values.website || 'No website address'} list={true} type="text" />
              <OverviewCardDivider />
              <OverviewCardSubTitle title="Addresses" />
              <OverviewField label="Primary" value={addressUtil(values.address)} type="text" />
              <OverviewCardDivider />
              <OverviewCardSubTitle
                title="Primary Contact"
                linkTabLabel="Contacts"
                linkTabId="contacts"
                tabPanelRef={tabPanelRef}
              />
              <OverviewField value={primaryContact.name?.fullName || 'No primary contact'} list={true} type="text" />
              <OverviewField value={primaryContact.email} list={true} type="text" />
              <OverviewField value={primaryContact.telephoneNumber} list={true} type="text" />
            </OverviewCard>
          </Form.Fieldset>
          <Form.Fieldset cols={cols}>
            {purchaseOrdersPermitted ? (
              <OverviewCard>
                <OverviewCardTitle
                  title="Purchases"
                  linkTabId="purchases"
                  linkTabLabel="Orders"
                  tabPanelRef={tabPanelRef}
                />
                <Form.Row style={{ paddingRight: '10px' }}>
                  <Form.Fieldset cols={1 / 3}>
                    <ValueCard
                      readOnly={true}
                      value={orders.orderValue}
                      label="Outstanding orders"
                      currency={currency}
                      decimals={2}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 3}>
                    <ValueCard
                      readOnly={true}
                      value={outstandingSum}
                      label="Invoices in dispute"
                      onClick={() => displayPurchaseTransactionModal(disputedInvoicesFilters)}
                      tooltip={'View disputed purchase transactions'}
                      tooltipFor={'supplierOverviewTooltip'}
                      currency={currency}
                      decimals={2}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 3}>
                    <ValueCard
                      readOnly={true}
                      value={awaitingApprovalSum}
                      label="Awaiting approval"
                      onClick={() => displayPurchaseInvoiceModal(invoicesAwaitingApprovalFilters)}
                      tooltip={'View invoices awaiting approval'}
                      tooltipFor={'supplierOverviewTooltip'}
                      currency={currency}
                      decimals={2}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </OverviewCard>
            ) : null}
            {tasksPermitted ? (
              <OverviewCard>
                <OverviewCardTitle title="Events" linkTabLabel="Events" linkTabId="events" tabPanelRef={tabPanelRef} />
                <OverviewLabel label={`Showing ${events.count} of ${events.totalCount} events`} />
                <OverviewCardDivider />
                {events.list}
              </OverviewCard>
            ) : null}
          </Form.Fieldset>
          <Form.Fieldset cols={cols}>
            <OverviewCard>
              <OverviewCardTitle
                title="Credit Control"
                linkTabLabel="Balances"
                linkTabId="credit"
                tabPanelRef={tabPanelRef}
              />
              <Form.Row style={{ paddingRight: '10px' }}>
                <Form.Fieldset cols={1 / 2}>
                  <ValueCard
                    readOnly={true}
                    value={values.balance?.balance}
                    label="Total balance"
                    decimals={2}
                    type={'currency'}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  <ValueCard
                    readOnly={true}
                    value={values.creditLimit}
                    label="Credit limit"
                    decimals={2}
                    type={'currency'}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row style={{ paddingRight: '10px' }}>
                <Form.Fieldset cols={1 / 2}>
                  <ValueCard
                    readOnly={true}
                    value={overdueBalance}
                    label="Overdue balance"
                    onClick={() => displayPurchaseTransactionModal(overdueBalanceFilters)}
                    tooltip={'View overdue transactions'}
                    tooltipFor={'supplierOverviewTooltip'}
                    decimals={2}
                    color={overdueBalance > 0 ? theme.colors.notificationWarning : null}
                    type={'currency'}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  <ValueCard readOnly={true} value={currency.description || 'GBP'} label="Currency" type={'text'} />
                </Form.Fieldset>
              </Form.Row>
              {transactionsPermitted ? (
                <>
                  <OverviewCardDivider />
                  <OverviewCardSubTitle
                    title="Status"
                    linkTabLabel="Transactions"
                    linkTabId="transactions"
                    tabPanelRef={tabPanelRef}
                  />
                  <OverviewInlineField
                    label="Last transaction"
                    value={formatDate(getIn(values, 'lastTransactionDate'))}
                    type="text"
                    fontSize="13px"
                  />
                  <OverviewInlineField
                    label="Payment terms"
                    value={getIn(values, 'paymentTerm.description')}
                    type="text"
                    fontSize="13px"
                  />
                  <OverviewInlineField
                    label="Account status"
                    value={getIn(values, 'supplierAccountStatus.description')}
                    type="text"
                    fontSize="13px"
                  />
                  <OverviewInlineField
                    label="VAT status"
                    value={getIn(values, 'vatBusinessCategory.description')}
                    type="text"
                    fontSize="13px"
                  />
                  <OverviewInlineField
                    label="Nominal category"
                    value={getIn(values, 'nominalBusinessCategory.description')}
                    type="text"
                    fontSize="13px"
                  />
                  <OverviewInlineField
                    label="Purchase nominal"
                    value={getIn(values, 'purchaseNominal.name')}
                    type="text"
                    fontSize="13px"
                  />
                </>
              ) : null}
            </OverviewCard>
          </Form.Fieldset>
        </Form.Row>
      </OverviewContainer>
    </CurrencyContext.Provider>
  );
};

export default SupplierOverviewTab;
